import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { Button, Input, Space, Tooltip } from "antd";
import { CheckOutlined, CloseOutlined } from "@ant-design/icons";
import { AxiosError } from "axios";
import Highlighter from "react-highlight-words";

import { EmitSuccessAlert } from "../../utils/EmitSuccessAlert";
import { EmitError } from "../../utils/EmitError";
import { formatDateStringToDateAndTime } from "../../utils/Conversores";
import SubjectHelpAndSupportService from "../../services/http/services/SubjectHelpAndSupportService";
import { ISubjectContact } from "../HelpAndSupport/interfaces";
import TableActionButton from "../../components/TableActionButton";
import { ThemeContext } from "../../contexts/ThemeContext";
import { IActiveFilterItem } from "../../components/ActiveFiltersBar";

export interface ITableSubjectData extends ISubjectContact {
    key: number;
}

export default function useSubjectHelpAndSupport() {
    const { theme } = useContext(ThemeContext);
    const isDark = theme === "dark";

    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [isLoadingDelete, setIsLoadingDelete] = useState<boolean>(false);
    const [isDeleteModalVisible, setIsDeleteModalVisible] = useState<boolean>(false);
    const [tableData, setTableData] = useState<Array<ITableSubjectData>>([]);
    const [subjectBeingDeleted, setSubjectBeingDeleted] = useState<ITableSubjectData | null>(null);

    // Modal de Criação Rápida
    const [isCreateModalVisible, setIsCreateModalVisible] = useState<boolean>(false);
    const [isLoadingCreate, setIsLoadingCreate] = useState<boolean>(false);
    const [newSubjectName, setNewSubjectName] = useState<string>("");

    // Edição Inline na Tabela
    const [editingId, setEditingId] = useState<number | null>(null);
    const [editingName, setEditingName] = useState<string>("");
    const [isLoadingInlineSave, setIsLoadingInlineSave] = useState<boolean>(false);

    const [searchText, setSearchText] = useState<string>("");
    const [currentPage, setCurrentPage] = useState<number>(1);
    const [pageSize, setPageSize] = useState<number>(10);

    const loadSubjects = useCallback(async (signal?: AbortSignal) => {
        try {
            const controller = signal ? undefined : new AbortController();
            const subjects = await SubjectHelpAndSupportService.listSubjects(
                signal || controller!.signal
            );
            const mappedData: ITableSubjectData[] = (subjects || []).map(
                (item: ISubjectContact) => ({
                    ...item,
                    key: item.id || Math.random(),
                })
            );
            setTableData(mappedData);
        } catch (err) {
            if (err instanceof AxiosError && err.name === "CanceledError") {
                return;
            }
            EmitError(err, true, "subjects_load_error");
        } finally {
            setIsLoading(false);
        }
    }, []);

    useEffect(() => {
        const controller = new AbortController();
        setIsLoading(true);
        loadSubjects(controller.signal);

        return () => {
            controller.abort();
        };
    }, [loadSubjects]);

    // Modal Criação
    const handleOpenCreateModal = useCallback(() => {
        setNewSubjectName("");
        setIsCreateModalVisible(true);
    }, []);

    const handleCloseCreateModal = useCallback(() => {
        setIsCreateModalVisible(false);
        setNewSubjectName("");
    }, []);

    const handleSaveNewSubject = useCallback(async () => {
        const trimmed = newSubjectName.trim();
        if (!trimmed) return;

        try {
            setIsLoadingCreate(true);
            await SubjectHelpAndSupportService.createSubject(trimmed);
            EmitSuccessAlert("Assunto cadastrado com sucesso!");
            handleCloseCreateModal();
            await loadSubjects();
        } catch (err) {
            EmitError(err, true, "subject_create_error");
        } finally {
            setIsLoadingCreate(false);
        }
    }, [newSubjectName, handleCloseCreateModal, loadSubjects]);

    // Edição Inline
    const handleStartInlineEdit = useCallback((record: ITableSubjectData) => {
        if (!record.id) return;
        setEditingId(record.id);
        setEditingName(record.name || "");
    }, []);

    const handleCancelInlineEdit = useCallback(() => {
        setEditingId(null);
        setEditingName("");
    }, []);

    const handleSaveInlineEdit = useCallback(async () => {
        if (!editingId) return;
        const trimmed = editingName.trim();
        if (!trimmed) {
            EmitError("O nome do assunto não pode ficar vazio.", false);
            return;
        }

        const original = tableData.find((item) => item.id === editingId);
        if (original && original.name === trimmed) {
            // Nenhuma alteração real
            setEditingId(null);
            setEditingName("");
            return;
        }

        try {
            setIsLoadingInlineSave(true);
            const updated = await SubjectHelpAndSupportService.updateSubject(editingId, {
                name: trimmed,
            });
            EmitSuccessAlert("Assunto alterado com sucesso!");
            setTableData((prev) =>
                prev.map((item) =>
                    item.id === editingId
                        ? { ...item, name: trimmed, updated_at: updated?.updated_at || item.updated_at }
                        : item
                )
            );
            setEditingId(null);
            setEditingName("");
        } catch (err) {
            EmitError(err, true, "subject_inline_update_error");
        } finally {
            setIsLoadingInlineSave(false);
        }
    }, [editingId, editingName, tableData]);

    // Exclusão
    const handleDeleteSubject = useCallback((data: ITableSubjectData) => {
        setSubjectBeingDeleted(data);
        setIsDeleteModalVisible(true);
    }, []);

    const handleConfirmDeleteSubject = useCallback(async () => {
        if (!subjectBeingDeleted?.id) return;
        try {
            setIsLoadingDelete(true);
            await SubjectHelpAndSupportService.deleteSubject(subjectBeingDeleted.id);
            EmitSuccessAlert("Assunto removido com sucesso!");
            setTableData((prev) => prev.filter((item) => item.id !== subjectBeingDeleted.id));
            setIsDeleteModalVisible(false);
            setSubjectBeingDeleted(null);
        } catch (err) {
            EmitError(err, true, "subjects_delete_error");
        } finally {
            setIsLoadingDelete(false);
        }
    }, [subjectBeingDeleted]);

    const handleCancelDeleteSubject = useCallback(() => {
        setIsDeleteModalVisible(false);
        setSubjectBeingDeleted(null);
    }, []);

    const handleClearAllFilters = useCallback(() => {
        setSearchText("");
        setCurrentPage(1);
    }, []);

    // Filter table data
    const filteredData = useMemo(() => {
        let result = tableData;
        if (searchText && searchText.trim()) {
            const query = searchText.toLowerCase().trim();
            result = result.filter((item) => item.name?.toLowerCase().includes(query));
        }
        return result;
    }, [tableData, searchText]);

    // Active Filter Items
    const activeFilterItems = useMemo((): IActiveFilterItem[] => {
        const items: IActiveFilterItem[] = [];
        if (searchText.trim()) {
            items.push({
                key: "search",
                label: "Busca",
                value: `"${searchText}"`,
                onClear: () => {
                    setSearchText("");
                    setCurrentPage(1);
                },
            });
        }
        return items;
    }, [searchText]);

    const columns = useMemo(
        () => [
            {
                title: "Nome",
                dataIndex: "name",
                key: "name",
                sorter: (a: ITableSubjectData, b: ITableSubjectData) =>
                    (a.name || "").localeCompare(b.name || ""),
                render: (text: string, record: ITableSubjectData) => {
                    const isEditing = editingId === record.id;

                    if (isEditing) {
                        return (
                            <div
                                onClick={(e) => e.stopPropagation()}
                                style={{ display: "flex", alignItems: "center", gap: 8 }}
                            >
                                <Input
                                    value={editingName}
                                    onChange={(e) => setEditingName(e.target.value)}
                                    onKeyDown={(e) => {
                                        if (e.key === "Enter") {
                                            e.preventDefault();
                                            handleSaveInlineEdit();
                                        } else if (e.key === "Escape") {
                                            e.preventDefault();
                                            handleCancelInlineEdit();
                                        }
                                    }}
                                    placeholder="Nome do assunto..."
                                    autoFocus
                                    disabled={isLoadingInlineSave}
                                    style={{
                                        maxWidth: 420,
                                        borderRadius: 6,
                                        fontWeight: 600,
                                    }}
                                />
                                <span style={{ fontSize: 11, color: isDark ? "#8c8c8c" : "#94a3b8" }}>
                                    (Enter p/ salvar, Esc p/ cancelar)
                                </span>
                            </div>
                        );
                    }

                    return (
                        <div
                            style={{
                                display: "inline-flex",
                                alignItems: "center",
                                gap: 6,
                                cursor: "pointer",
                            }}
                            title="Dê um duplo clique para editar"
                        >
                            <span style={{ fontWeight: 600, fontSize: 13.5, color: isDark ? "#f0f0f0" : "#1e293b" }}>
                                <Highlighter
                                    highlightStyle={{ backgroundColor: "#ffc069", padding: "0 2px" }}
                                    searchWords={searchText ? [searchText] : []}
                                    autoEscape
                                    textToHighlight={text || "--"}
                                />
                            </span>
                        </div>
                    );
                },
            },
            {
                title: "Data de Cadastro",
                dataIndex: "created_at",
                key: "created_at",
                width: 220,
                sorter: (a: ITableSubjectData, b: ITableSubjectData) =>
                    new Date(a.created_at || 0).getTime() - new Date(b.created_at || 0).getTime(),
                render: (criado: string) => (
                    <span style={{ color: isDark ? "#8c8c8c" : "#64748b", fontSize: 12.5 }}>
                        {criado ? formatDateStringToDateAndTime(criado) : "--"}
                    </span>
                ),
            },
            {
                title: "Ações",
                key: "actions",
                width: 130,
                align: "center" as const,
                render: (_: any, record: ITableSubjectData) => {
                    const isEditing = editingId === record.id;

                    if (isEditing) {
                        return (
                            <div
                                onClick={(e) => e.stopPropagation()}
                                style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 6 }}
                            >
                                <Tooltip title="Salvar alteração (Enter)">
                                    <Button
                                        type="primary"
                                        size="small"
                                        icon={<CheckOutlined />}
                                        loading={isLoadingInlineSave}
                                        onClick={handleSaveInlineEdit}
                                        style={{
                                            backgroundColor: "#10b981",
                                            borderColor: "#10b981",
                                            borderRadius: 6,
                                            height: 28,
                                            width: 28,
                                            display: "inline-flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                        }}
                                    />
                                </Tooltip>
                                <Tooltip title="Cancelar edição (Esc)">
                                    <Button
                                        size="small"
                                        icon={<CloseOutlined />}
                                        disabled={isLoadingInlineSave}
                                        onClick={handleCancelInlineEdit}
                                        style={{
                                            borderRadius: 6,
                                            height: 28,
                                            width: 28,
                                            display: "inline-flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                        }}
                                    />
                                </Tooltip>
                            </div>
                        );
                    }

                    return (
                        <div onClick={(e) => e.stopPropagation()}>
                            <Space size={6}>
                                <TableActionButton
                                    action="edit"
                                    tooltip="Editar assunto direto na tabela"
                                    onClick={() => handleStartInlineEdit(record)}
                                />
                                <TableActionButton
                                    action="delete"
                                    tooltip="Excluir assunto"
                                    onClick={() => handleDeleteSubject(record)}
                                />
                            </Space>
                        </div>
                    );
                },
            },
        ],
        [
            editingId,
            editingName,
            isLoadingInlineSave,
            isDark,
            searchText,
            handleSaveInlineEdit,
            handleCancelInlineEdit,
            handleStartInlineEdit,
            handleDeleteSubject,
        ]
    );

    return {
        isLoading,
        isLoadingDelete,
        isDeleteModalVisible,
        subjectBeingDeleted,
        isCreateModalVisible,
        isLoadingCreate,
        newSubjectName,
        setNewSubjectName,
        editingId,
        editingName,
        setEditingName,
        isLoadingInlineSave,
        columns,
        tableData,
        filteredData,
        searchText,
        setSearchText,
        currentPage,
        setCurrentPage,
        pageSize,
        setPageSize,
        activeFilterItems,
        handleOpenCreateModal,
        handleCloseCreateModal,
        handleSaveNewSubject,
        handleStartInlineEdit,
        handleCancelInlineEdit,
        handleSaveInlineEdit,
        handleDeleteSubject,
        handleConfirmDeleteSubject,
        handleCancelDeleteSubject,
        handleClearAllFilters,
    };
}

